import { useState, useEffect } from 'react';
import ManageRoomsServices from './Role-based/ManageRoomServices';
import { FaEdit, FaTrash, FaImage, FaSearch } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function ManageRoomCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [imageErrors, setImageErrors] = useState({});
  const [loadedImages, setLoadedImages] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [currentCategory, setCurrentCategory] = useState({
    id: null,
    name: '',
    description: '',
    isactive: true,
    propertyid: 1,
    file: null,
    existingImage: '',
  });
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    isactive: true,
    propertyid: 1,
    file: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-categories/`);
        if (!response.ok) throw new Error('Failed to fetch');
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        toast.error('Failed to fetch categories: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleImageError = (id) => {
    setImageErrors((prev) => ({ ...prev, [id]: true }));
  };

  const handleImageLoad = (id) => {
    setLoadedImages((prev) => ({ ...prev, [id]: true }));
  };

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/room-service-categories/${categoryToDelete.id}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        setCategories(categories.filter((cat) => cat.id !== categoryToDelete.id));
        toast.success(`Category "${categoryToDelete.name}" deleted successfully`);
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast.error(`Failed to delete category "${categoryToDelete.name}": ${error.message}`);
    } finally {
      setIsDeleteModalOpen(false);
      setCategoryToDelete(null);
    }
  };

  const handleEditClick = (category) => {
    setCurrentCategory({
      id: category.id,
      name: category.name,
      description: category.description,
      isactive: category.isactive,
      propertyid: category.propertyid,
      file: null,
      existingImage: category.imageurl,
    });
    setIsEditModalOpen(true);
  };

  const handleInputChange = (e, isNew = false) => {
    const { name, value, type, checked } = e.target;
    if (isNew) {
      setNewCategory((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    } else {
      setCurrentCategory((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleFileChange = (e, isNew = false) => {
    if (isNew) {
      setNewCategory((prev) => ({
        ...prev,
        file: e.target.files[0],
      }));
    } else {
      setCurrentCategory((prev) => ({
        ...prev,
        file: e.target.files[0],
      }));
    }
  };

  const handleUpdateCategory = async () => {
    if (!currentCategory.name) {
      toast.error('Name is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      if (currentCategory.file) {
        formData.append('file', currentCategory.file);
      }

      const queryParams = new URLSearchParams({
        name: currentCategory.name,
        description: currentCategory.description,
        propertyid: currentCategory.propertyid,
        isactive: currentCategory.isactive,
      }).toString();

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/room-service-categories/${currentCategory.id}?${queryParams}`,
        {
          method: 'PUT',
          body: currentCategory.file ? formData : null,
        }
      );

      if (!response.ok) throw new Error('Failed to update category');

      const data = await response.json();

      setCategories(categories.map((cat) => (cat.id === currentCategory.id ? data : cat)));

      toast.success('Category updated successfully');
      setIsEditModalOpen(false);
    } catch (error) {
      toast.error('Failed to update category: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name) {
      toast.error('Name is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      if (newCategory.file) {
        formData.append('file', newCategory.file);
      }

      const queryParams = new URLSearchParams({
        name: newCategory.name,
        description: newCategory.description,
        propertyid: newCategory.propertyid,
        isactive: newCategory.isactive,
      }).toString();

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/room-service-categories/?${queryParams}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) throw new Error('Failed to add category');

      const data = await response.json();
      setCategories([...categories, data]);
      toast.success('Category added successfully');
      setIsAddModalOpen(false);
      setNewCategory({
        name: '',
        description: '',
        isactive: true,
        propertyid: 1,
        file: null,
      });
    } catch (error) {
      toast.error('Failed to add category: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className=" mx-auto p-6">
      <ManageRoomsServices />
      <ToastContainer position="top-right" autoClose={3000} />

      <h1 className="text-2xl font-bold mt-12 mb-6">Room Service Categories</h1>

      <div className="flex items-center gap-4 mb-8">
        <div className="relative flex-grow max-w-md">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search Categories..."
            className="border rounded-lg py-2 pl-10 pr-4 w-full"
          />
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-black hover:bg-black text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2"
        >
          <span>+</span> Add Category
        </button>
      </div>

      <div className="mb-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500 text-lg">No categories found</p>
            {/* {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="text-gray-800 mt-2 hover:underline"
              >
                Clear search
              </button>
            )} */}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCategories.map((category) => (
              <div
                key={category.id}
                className="border rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 bg-white"
              >
                <div className="relative w-full h-48 bg-gray-100">
                  {category.imageurl && !imageErrors[category.id] ? (
                    <>
                      <img
                        src={category.imageurl}
                        alt={category.name}
                        className={`w-full h-full object-cover ${
                          loadedImages[category.id] ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={{ transition: 'opacity 0.3s ease' }}
                        onLoad={() => handleImageLoad(category.id)}
                        onError={() => handleImageError(category.id)}
                        loading="lazy"
                      />
                      {!loadedImages[category.id] && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="animate-pulse bg-gray-200 w-full h-full"></div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gray-200">
                      <FaImage className="text-4xl mb-2" />
                      <span>No Image Available</span>
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      className="p-2 bg-white rounded-full shadow hover:bg-gray-100 transition-colors"
                      onClick={() => handleEditClick(category)}
                    >
                      <FaEdit className="h-4 w-4 text-blue-500" />
                    </button>
                    <button
                      className="p-2 bg-white rounded-full shadow hover:bg-gray-100 transition-colors"
                      onClick={() => handleDeleteClick(category)}
                    >
                      <FaTrash className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-lg text-gray-800">{category.name}</h3>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        category.isactive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {category.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {category.description || 'No description available'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-[500px]">
            <h2 className="text-xl font-bold mb-4">Add New Category</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={newCategory.name}
                  placeholder="Enter category name here."
                  onChange={(e) => handleInputChange(e, true)}
                  className="w-full border rounded-lg px-3 py-2 placeholder:text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  name="description"
                  value={newCategory.description}
                  placeholder="Enter category description here."
                  onChange={(e) => handleInputChange(e, true)}
                  className="w-full border rounded-lg px-3 py-2 placeholder:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, true)}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Customer Visibility
                  </label>
                  <p className="text-xs text-gray-500">
                    Toggle to show or hide this category from customers.
                  </p>
                </div>
                <label
                  htmlFor="isActive"
                  className="relative inline-flex items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isactive"
                    checked={newCategory.isactive}
                    onChange={(e) => handleInputChange(e, true)}
                    className="sr-only"
                  />
                  <div
                    className={`w-12 h-6 rounded-full transition-colors ${
                      newCategory.isactive ? 'bg-black' : 'bg-gray-300'
                    }`}
                  ></div>
                  <div
                    className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      newCategory.isactive ? 'translate-x-6' : ''
                    }`}
                  ></div>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsAddModalOpen(false)}
                disabled={isSubmitting}
                className="bg-red-500 text-white px-4 py-2 border rounded-lg hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                disabled={isSubmitting}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-black disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting && (
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                Save Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[95vh] ">
            <h2 className="text-xl font-bold mb-2">Edit Category</h2>
            <div className="space-y-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={currentCategory.name}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={2}
                  name="description"
                  value={currentCategory.description}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                {currentCategory.existingImage && (
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 mb-1">Current Image:</p>
                    <img
                      src={currentCategory.existingImage}
                      alt="Current"
                      className="h-15 w-24 object-cover rounded border"
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Leave as it is to keep current image.</p>
              </div>
              <div className="flex items-center justify-between py-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Visibility
                  </label>
                  <p className="text-xs text-gray-500">
                    Toggle to show or hide this category from customers
                  </p>
                </div>
                <label
                  htmlFor="editIsActive"
                  className="relative inline-flex items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    id="editIsActive"
                    name="isactive"
                    checked={currentCategory.isactive}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div
                    className={`w-12 h-6 rounded-full transition-colors ${
                      currentCategory.isactive ? 'bg-black' : 'bg-gray-300'
                    }`}
                  ></div>
                  <div
                    className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      currentCategory.isactive ? 'translate-x-6' : ''
                    }`}
                  ></div>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsEditModalOpen(false)}
                disabled={isSubmitting}
                className="bg-red-500 text-white px-4 py-2 border text-sm rounded-lg hover:text-black disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCategory}
                disabled={isSubmitting}
                className="px-4 py-2 bg-black text-white rounded-lg text-sm hover:text-white disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting && (
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && categoryToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p className="mb-6">
              Are you sure you want to delete the category{' '}
              <span className="font-semibold">"{categoryToDelete.name}"</span>? This action cannot
              be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="bg-black text-white px-4 py-2 border rounded-lg hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:text-black flex items-center gap-2"
              >
                <FaTrash className="h-4 w-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageRoomCategories;
